import React, { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useUser } from '../../../../UserContext';
import { 
    Megaphone, 
    Plus, 
    Edit3, 
    Trash2, 
    ToggleLeft, 
    ToggleRight,
    X,
    AlertTriangle,
    Info,
    CheckCircle,
    Tag,
    Wrench,
    ArrowLeft,
    ExternalLink
} from 'lucide-react';
import './Admin.css';
import { adminService } from '../../../../services';

const bannerLinkOptions = [
    { value: '', label: 'No action', url: '' },
    { value: 'subscription', label: 'Subscription page', url: '/dashboard/subscription' },
    { value: 'home', label: 'Dashboard home', url: '/dashboard/home' },
    { value: 'blog', label: 'Blog', url: '/dashboard/blog' },
    { value: 'score', label: 'Score events', url: '/dashboard/score' },
    { value: 'marketplace', label: 'Marketplace', url: '/dashboard/marketplace' },
    { value: 'custom', label: 'Custom URL', url: null }
];

const AdminBanners = () => {
    const { user } = useUser();
    const [banners, setBanners] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);
    const [showModal, setShowModal] = useState(false);
    const [editingBanner, setEditingBanner] = useState(null);
    const [formData, setFormData] = useState({
        title: '',
        message: '',
        linkDestination: '',
        linkUrl: '',
        linkText: '',
        backgroundColor: '#4CAF50',
        textColor: '#FFFFFF',
        type: 'Info',
        isActive: true,
        isDismissible: true,
        showOnWeb: true,
        showOnMobile: true,
        startDate: '',
        endDate: '',
        priority: 0
    });

    const bannerTypes = [
        { value: 'Info', label: 'Information', icon: Info, color: '#3b82f6' },
        { value: 'Warning', label: 'Warning', icon: AlertTriangle, color: '#f59e0b' },
        { value: 'Success', label: 'Success', icon: CheckCircle, color: '#22c55e' },
        { value: 'Promotion', label: 'Promotion', icon: Tag, color: '#8b5cf6' },
        { value: 'Maintenance', label: 'Maintenance', icon: Wrench, color: '#ef4444' }
    ];

    useEffect(() => {
        fetchBanners();
    }, []);

    const fetchBanners = async () => {
        try {
            setIsLoading(true);
            const data = await adminService.getBanners();
            setBanners(data);
        } catch (err) {
            setError(err.message);
        } finally {
            setIsLoading(false);
        }
    };

    const resolveLinkDestination = (linkUrl) => {
        const matched = bannerLinkOptions.find((option) => option.url && option.url === linkUrl);
        return matched ? matched.value : (linkUrl ? 'custom' : '');
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        try {
            const payload = {
                ...formData,
                startDate: formData.startDate || null,
                endDate: formData.endDate || null
            };

            if (editingBanner) {
                await adminService.updateBanner(editingBanner.bannerId, payload);
            } else {
                await adminService.createBanner(payload);
            }
            
            await fetchBanners();
            closeModal();
        } catch (err) {
            setError(err.message);
        }
    };

    const handleDelete = async (bannerId) => {
        if (!window.confirm('Are you sure you want to delete this banner?')) return;

        try {
            await adminService.deleteBanner(bannerId);
            await fetchBanners();
        } catch (err) {
            setError(err.message);
        }
    };

    const handleToggleActive = async (bannerId) => {
        try {
            await adminService.toggleBanner(bannerId);
            await fetchBanners();
        } catch (err) {
            setError(err.message);
        }
    };

    const openCreateModal = () => {
        setEditingBanner(null);
        setFormData({
            title: '',
            message: '',
            linkDestination: '',
            linkUrl: '',
            linkText: '',
            backgroundColor: '#4CAF50',
            textColor: '#FFFFFF',
            type: 'Info',
            isActive: true,
            isDismissible: true,
            showOnWeb: true,
            showOnMobile: true,
            startDate: '',
            endDate: '',
            priority: 0
        });
        setShowModal(true);
    };

    const openEditModal = (banner) => {
        setEditingBanner(banner);
        setFormData({
            title: banner.title,
            message: banner.message,
            linkDestination: resolveLinkDestination(banner.linkUrl || ''),
            linkUrl: banner.linkUrl || '',
            linkText: banner.linkText || '',
            backgroundColor: banner.backgroundColor,
            textColor: banner.textColor,
            type: banner.type,
            isActive: banner.isActive,
            isDismissible: banner.isDismissible,
            showOnWeb: banner.showOnWeb ?? true,
            showOnMobile: banner.showOnMobile ?? true,
            startDate: banner.startDate ? banner.startDate.split('T')[0] : '',
            endDate: banner.endDate ? banner.endDate.split('T')[0] : '',
            priority: banner.priority
        });
        setShowModal(true);
    };

    const closeModal = () => {
        setShowModal(false);
        setEditingBanner(null);
    };

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        if (name === 'linkDestination') {
            const selectedOption = bannerLinkOptions.find((option) => option.value === value);
            setFormData((prev) => ({
                ...prev,
                linkDestination: value,
                linkUrl: selectedOption?.value === 'custom' ? prev.linkUrl : (selectedOption?.url || '')
            }));
            return;
        }
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const getBannerTypeInfo = (type) => {
        return bannerTypes.find(t => t.value === type) || bannerTypes[0];
    };

    if (!user?.isAdmin) {
        return (
            <div className="admin-page">
                <div className="admin-access-denied">
                    <AlertTriangle size={48} />
                    <h2>Access Denied</h2>
                    <p>You do not have permission to access this page.</p>
                </div>
            </div>
        );
    }

    return (
        <div className="admin-page">
            <div className="admin-header">
                <div className="admin-header-content">
                    <Link to="/dashboard/admin" className="admin-back-btn">
                        <ArrowLeft size={20} />
                    </Link>
                    <div className="admin-header-icon">
                        <Megaphone size={28} />
                    </div>
                    <div>
                        <h1>Manage Banners</h1>
                        <p>Create and manage dashboard-home announcements</p>
                    </div>
                </div>
                <button onClick={openCreateModal} className="admin-btn admin-btn-primary">
                    <Plus size={20} />
                    Create Banner
                </button>
            </div>

            {error && (
                <div className="admin-alert admin-alert-error">
                    <AlertTriangle size={20} />
                    {error}
                    <button onClick={() => setError(null)}><X size={16} /></button>
                </div>
            )}

            {isLoading ? (
                <div className="admin-loading">
                    <div className="admin-spinner"></div>
                    <p>Loading banners...</p>
                </div>
            ) : banners.length === 0 ? (
                <div className="admin-empty-state">
                    <Megaphone size={64} />
                    <h3>No Banners Yet</h3>
                    <p>Create your first banner to display announcements across the platform.</p>
                    <button onClick={openCreateModal} className="admin-btn admin-btn-primary">
                        <Plus size={20} />
                        Create First Banner
                    </button>
                </div>
            ) : (
                <div className="admin-banners-list">
                    {banners.map(banner => {
                        const typeInfo = getBannerTypeInfo(banner.type);
                        const TypeIcon = typeInfo.icon;
                        return (
                            <div 
                                key={banner.bannerId} 
                                className={`admin-banner-card ${!banner.isActive ? 'inactive' : ''}`}
                            >
                                <div 
                                    className="admin-banner-preview"
                                    style={{ 
                                        backgroundColor: banner.backgroundColor,
                                        color: banner.textColor
                                    }}
                                >
                                    <strong>{banner.title}</strong>
                                    <span>{banner.message}</span>
                                    {banner.linkUrl && (
                                        <a href={banner.linkUrl} target={banner.linkUrl.startsWith('/dashboard') ? '_self' : '_blank'} rel={banner.linkUrl.startsWith('/dashboard') ? undefined : 'noopener noreferrer'}>
                                            {banner.linkText || 'Learn More'} <ExternalLink size={14} />
                                        </a>
                                    )}
                                </div>
                                <div className="admin-banner-details">
                                    <div className="admin-banner-meta">
                                        <span className="admin-banner-type" style={{ color: typeInfo.color }}>
                                            <TypeIcon size={16} />
                                            {typeInfo.label}
                                        </span>
                                        <span className={`admin-banner-status ${banner.isActive ? 'active' : 'inactive'}`}>
                                            {banner.isActive ? 'Active' : 'Inactive'}
                                        </span>
<span className="admin-banner-priority">
                                                            Priority: {banner.priority}
                                                        </span>
                                                        <span className="admin-banner-platforms">
                                                            {banner.showOnWeb && <span className="platform-badge web">Web</span>}
                                                            {banner.showOnMobile && <span className="platform-badge mobile">Mobile</span>}
                                                            {!banner.showOnWeb && !banner.showOnMobile && <span className="platform-badge none">No platforms</span>}
                                                        </span>
                                                    </div>
                                    {(banner.startDate || banner.endDate) && (
                                        <div className="admin-banner-schedule">
                                            {banner.startDate && (
                                                <span>From: {new Date(banner.startDate).toLocaleDateString()}</span>
                                            )}
                                            {banner.endDate && (
                                                <span>Until: {new Date(banner.endDate).toLocaleDateString()}</span>
                                            )}
                                        </div>
                                    )}
                                    <div className="admin-banner-actions">
                                        <button 
                                            onClick={() => handleToggleActive(banner.bannerId)}
                                            className="admin-btn admin-btn-icon"
                                            title={banner.isActive ? 'Deactivate' : 'Activate'}
                                        >
                                            {banner.isActive ? <ToggleRight size={20} /> : <ToggleLeft size={20} />}
                                        </button>
                                        <button 
                                            onClick={() => openEditModal(banner)}
                                            className="admin-btn admin-btn-icon"
                                            title="Edit"
                                        >
                                            <Edit3 size={20} />
                                        </button>
                                        <button 
                                            onClick={() => handleDelete(banner.bannerId)}
                                            className="admin-btn admin-btn-icon admin-btn-danger"
                                            title="Delete"
                                        >
                                            <Trash2 size={20} />
                                        </button>
                                    </div>
                                </div>
                            </div>
                        );
                    })}
                </div>
            )}

            {showModal && (
                <div className="admin-modal-overlay" onClick={closeModal}>
                    <div className="admin-modal" onClick={e => e.stopPropagation()}>
                        <div className="admin-modal-header">
                            <h2>{editingBanner ? 'Edit Banner' : 'Create New Banner'}</h2>
                            <button onClick={closeModal} className="admin-modal-close">
                                <X size={24} />
                            </button>
                        </div>
                        <form onSubmit={handleSubmit} className="admin-modal-form">
                            <div className="admin-form-group">
                                <label>Title *</label>
                                <input
                                    type="text"
                                    name="title"
                                    value={formData.title}
                                    onChange={handleInputChange}
                                    required
                                    maxLength={100}
                                    placeholder="Enter banner title"
                                />
                            </div>

                            <div className="admin-form-group">
                                <label>Message *</label>
                                <textarea
                                    name="message"
                                    value={formData.message}
                                    onChange={handleInputChange}
                                    required
                                    maxLength={500}
                                    rows={3}
                                    placeholder="Enter banner message"
                                />
                            </div>

                            <div className="admin-form-row">
                                <div className="admin-form-group">
                                    <label>Banner Action</label>
                                    <select
                                        name="linkDestination"
                                        value={formData.linkDestination}
                                        onChange={handleInputChange}
                                    >
                                        {bannerLinkOptions.map((option) => (
                                            <option key={option.value || 'none'} value={option.value}>
                                                {option.label}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div className="admin-form-group">
                                    <label>Link Text</label>
                                    <input
                                        type="text"
                                        name="linkText"
                                        value={formData.linkText}
                                        onChange={handleInputChange}
                                        placeholder="Learn More"
                                        maxLength={50}
                                    />
                                </div>
                            </div>

                            {formData.linkDestination === 'custom' && (
                                <div className="admin-form-group">
                                    <label>Custom URL</label>
                                    <input
                                        type="url"
                                        name="linkUrl"
                                        value={formData.linkUrl}
                                        onChange={handleInputChange}
                                        placeholder="https://example.com"
                                    />
                                </div>
                            )}

                            <div className="admin-form-row">
                                <div className="admin-form-group">
                                    <label>Background Color</label>
                                    <div className="admin-color-input">
                                        <input
                                            type="color"
                                            name="backgroundColor"
                                            value={formData.backgroundColor}
                                            onChange={handleInputChange}
                                        />
                                        <input
                                            type="text"
                                            value={formData.backgroundColor}
                                            onChange={handleInputChange}
                                            name="backgroundColor"
                                        />
                                    </div>
                                </div>
                                <div className="admin-form-group">
                                    <label>Text Color</label>
                                    <div className="admin-color-input">
                                        <input
                                            type="color"
                                            name="textColor"
                                            value={formData.textColor}
                                            onChange={handleInputChange}
                                        />
                                        <input
                                            type="text"
                                            value={formData.textColor}
                                            onChange={handleInputChange}
                                            name="textColor"
                                        />
                                    </div>
                                </div>
                            </div>

                            <div className="admin-form-row">
                                <div className="admin-form-group">
                                    <label>Banner Type</label>
                                    <select
                                        name="type"
                                        value={formData.type}
                                        onChange={handleInputChange}
                                    >
                                        {bannerTypes.map(type => (
                                            <option key={type.value} value={type.value}>
                                                {type.label}
                                            </option>
                                        ))}
                                    </select>
                                </div>
                                <div className="admin-form-group">
                                    <label>Priority</label>
                                    <input
                                        type="number"
                                        name="priority"
                                        value={formData.priority}
                                        onChange={handleInputChange}
                                        min={0}
                                        max={100}
                                    />
                                    <small>Higher numbers show first</small>
                                </div>
                            </div>

                            <div className="admin-form-row">
                                <div className="admin-form-group">
                                    <label>Start Date (Optional)</label>
                                    <input
                                        type="date"
                                        name="startDate"
                                        value={formData.startDate}
                                        onChange={handleInputChange}
                                    />
                                </div>
                                <div className="admin-form-group">
                                    <label>End Date (Optional)</label>
                                    <input
                                        type="date"
                                        name="endDate"
                                        value={formData.endDate}
                                        onChange={handleInputChange}
                                    />
                                </div>
                            </div>

                            <div className="admin-form-checkboxes">
                                <label className="admin-checkbox">
                                    <input
                                        type="checkbox"
                                        name="isActive"
                                        checked={formData.isActive}
                                        onChange={handleInputChange}
                                    />
                                    <span>Active</span>
                                </label>
                                <label className="admin-checkbox">
                                    <input
                                        type="checkbox"
                                        name="isDismissible"
                                        checked={formData.isDismissible}
                                        onChange={handleInputChange}
                                    />
                                    <span>Users can dismiss</span>
                                </label>
                                <label className="admin-checkbox">
                                    <input
                                        type="checkbox"
                                        name="showOnWeb"
                                        checked={formData.showOnWeb}
                                        onChange={handleInputChange}
                                    />
                                    <span>Show on desktop / web</span>
                                </label>
                                <label className="admin-checkbox">
                                    <input
                                        type="checkbox"
                                        name="showOnMobile"
                                        checked={formData.showOnMobile}
                                        onChange={handleInputChange}
                                    />
                                    <span>Show on Mobile</span>
                                </label>
                            </div>

                            <div className="admin-form-helper">
                                Banners appear on the logged-in dashboard home only. Subscription promos are hidden for premium users.
                            </div>

                            <div className="admin-form-preview">
                                <label>Preview</label>
                                <div 
                                    className="admin-banner-preview"
                                    style={{ 
                                        backgroundColor: formData.backgroundColor,
                                        color: formData.textColor
                                    }}
                                >
                                    <strong>{formData.title || 'Banner Title'}</strong>
                                    <span>{formData.message || 'Banner message will appear here'}</span>
                                    {formData.linkUrl && (
                                        <span className="preview-link">
                                            {formData.linkText || 'Learn More'} <ExternalLink size={14} />
                                        </span>
                                    )}
                                </div>
                            </div>

                            <div className="admin-modal-actions">
                                <button type="button" onClick={closeModal} className="admin-btn admin-btn-secondary">
                                    Cancel
                                </button>
                                <button type="submit" className="admin-btn admin-btn-primary">
                                    {editingBanner ? 'Save Changes' : 'Create Banner'}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}
        </div>
    );
};

export default AdminBanners;
